import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Filter, Download, Eye, Clock, CheckCircle, XCircle,
  Truck, ChefHat, Package, MoreVertical, RefreshCw,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { api } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

const statusConfig: Record<string, { icon: any; bgClass: string; textClass: string }> = {
  pending: { icon: Clock, bgClass: 'bg-amber-100 dark:bg-amber-900/30', textClass: 'text-amber-600 dark:text-amber-400' },
  confirmed: { icon: CheckCircle, bgClass: 'bg-blue-100 dark:bg-blue-900/30', textClass: 'text-blue-600 dark:text-blue-400' },
  preparing: { icon: ChefHat, bgClass: 'bg-indigo-100 dark:bg-indigo-900/30', textClass: 'text-indigo-600 dark:text-indigo-400' },
  ready: { icon: Package, bgClass: 'bg-cyan-100 dark:bg-cyan-900/30', textClass: 'text-cyan-600 dark:text-cyan-400' },
  on_the_way: { icon: Truck, bgClass: 'bg-purple-100 dark:bg-purple-900/30', textClass: 'text-purple-600 dark:text-purple-400' },
  delivered: { icon: CheckCircle, bgClass: 'bg-emerald-100 dark:bg-emerald-900/30', textClass: 'text-emerald-600 dark:text-emerald-400' },
  cancelled: { icon: XCircle, bgClass: 'bg-red-100 dark:bg-red-900/30', textClass: 'text-red-600 dark:text-red-400' },
};

const mockOrders = [
  { id: 1025, order_number: '#1025', customer: 'أحمد محمد', customer_phone: '01012345678', cafe: 'مقهى الورد', driver: 'خالد أحمد', amount: 125, status: 'pending', items_count: 3, created_at: '2026-03-22T10:30:00' },
  { id: 1024, order_number: '#1024', customer: 'سارة علي', customer_phone: '01098765432', cafe: 'كافيه الصباح', driver: 'محمد سعيد', amount: 85, status: 'preparing', items_count: 2, created_at: '2026-03-22T09:45:00' },
  { id: 1023, order_number: '#1023', customer: 'محمود حسن', customer_phone: '01155544433', cafe: 'بيت القهوة', driver: 'عمر يوسف', amount: 200, status: 'on_the_way', items_count: 5, created_at: '2026-03-22T09:15:00' },
  { id: 1022, order_number: '#1022', customer: 'فاطمة أحمد', customer_phone: '01234567890', cafe: 'مقهى النخيل', driver: 'أيمن محمود', amount: 150, status: 'delivered', items_count: 4, created_at: '2026-03-22T08:30:00' },
  { id: 1021, order_number: '#1021', customer: 'يوسف عبدالله', customer_phone: '01567890123', cafe: 'ستاربكس', driver: '--', amount: 95, status: 'cancelled', items_count: 1, created_at: '2026-03-22T08:00:00' },
  { id: 1020, order_number: '#1020', customer: 'نورا خالد', customer_phone: '01345678901', cafe: 'كافيه لاتيه', driver: 'حسام الدين', amount: 310, status: 'delivered', items_count: 7, created_at: '2026-03-21T22:15:00' },
  { id: 1019, order_number: '#1019', customer: 'عمر إبراهيم', customer_phone: '01089012345', cafe: 'مقهى الريحان', driver: 'سامي فؤاد', amount: 175, status: 'confirmed', items_count: 3, created_at: '2026-03-21T21:30:00' },
  { id: 1018, order_number: '#1018', customer: 'لين أحمد', customer_phone: '01178901234', cafe: 'بيت القهوة', driver: 'طارق حسن', amount: 65, status: 'ready', items_count: 2, created_at: '2026-03-21T20:45:00' },
];

export default function OrdersPage() {
  const { t, formatCurrency, language } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null);

  const { data: ordersData, isLoading, refetch } = useQuery({
    queryKey: ['orders', statusFilter],
    queryFn: () => api.getOrders({ page: 1, status: statusFilter === 'all' ? undefined : statusFilter }),
  });

  const orders = ordersData?.data || mockOrders;

  const filteredOrders = orders.filter((order: any) => {
    const matchSearch = !searchQuery || 
      order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.cafe?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statusCounts = {
    all: orders.length,
    pending: orders.filter((o: any) => o.status === 'pending').length,
    preparing: orders.filter((o: any) => o.status === 'preparing').length,
    on_the_way: orders.filter((o: any) => o.status === 'on_the_way').length,
    delivered: orders.filter((o: any) => o.status === 'delivered').length,
    cancelled: orders.filter((o: any) => o.status === 'cancelled').length,
  };

  const statusTabs = [
    { key: 'all', label: t('all') },
    { key: 'pending', label: t('pending') },
    { key: 'preparing', label: t('preparing') },
    { key: 'on_the_way', label: t('onTheWay') },
    { key: 'delivered', label: t('delivered') },
    { key: 'cancelled', label: t('cancelled') },
  ];

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(language === 'ar' ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('orders')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? `إجمالي ${orders.length} طلب` : `Total ${orders.length} orders`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <RefreshCw size={18} />
            <span className="hidden sm:inline">{language === 'ar' ? 'تحديث' : 'Refresh'}</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
            <Download size={18} />
            <span className="hidden sm:inline">{t('export')}</span>
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {statusTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === tab.key
                ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
          >
            {tab.label}
            <span className={`min-w-[24px] h-6 flex items-center justify-center rounded-full text-xs font-bold ${
              statusFilter === tab.key
                ? 'bg-white/20 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
            }`}>
              {statusCounts[tab.key as keyof typeof statusCounts] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Search & Filter Bar */}
      <Card>
        <div className="p-4 flex flex-col sm:flex-row items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute top-1/2 -translate-y-1/2 start-4 text-slate-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search')}
              className="w-full ps-12 pe-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          <button className="flex items-center gap-2 px-5 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <Filter size={18} />
            {t('filter')}
          </button>
        </div>
      </Card>

      {/* Orders Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700">
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('orderNumber')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('customer')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('cafe')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('driver')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('amount')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('status')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('date')}</th>
                <th className="text-start p-4 text-sm font-semibold text-slate-500 dark:text-slate-400">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="p-12 text-center">
                    <div className="inline-flex items-center gap-3 text-slate-500">
                      <RefreshCw className="animate-spin" size={20} />
                      {t('loading')}
                    </div>
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-12 text-center">
                    <div className="text-slate-400 dark:text-slate-500">
                      <Package size={48} className="mx-auto mb-3 opacity-50" />
                      <p className="font-medium">{t('noData')}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order: any) => {
                  const config = statusConfig[order.status] || statusConfig.pending;
                  const StatusIcon = config.icon;
                  return (
                    <tr
                      key={order.id}
                      className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)}
                    >
                      <td className="p-4">
                        <span className="font-bold text-primary-600 dark:text-primary-400">{order.order_number}</span>
                        <p className="text-xs text-slate-400 mt-0.5">{order.items_count} {language === 'ar' ? 'عناصر' : 'items'}</p>
                      </td>
                      <td className="p-4">
                        <p className="font-medium text-slate-800 dark:text-slate-200">{order.customer}</p>
                        <p className="text-xs text-slate-400">{order.customer_phone}</p>
                      </td>
                      <td className="p-4 text-slate-600 dark:text-slate-300">{order.cafe}</td>
                      <td className="p-4 text-slate-600 dark:text-slate-300">{order.driver || '--'}</td>
                      <td className="p-4">
                        <span className="font-bold text-slate-800 dark:text-slate-200">{formatCurrency(order.amount)}</span>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold ${config.bgClass} ${config.textClass}`}>
                          <StatusIcon size={14} />
                          {t(order.status === 'on_the_way' ? 'onTheWay' : order.status)}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-slate-600 dark:text-slate-300">{formatDate(order.created_at)}</p>
                        <p className="text-xs text-slate-400">{formatTime(order.created_at)}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors">
                            <Eye size={18} />
                          </button>
                          <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors">
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t border-slate-100 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {language === 'ar' 
              ? `عرض 1-${filteredOrders.length} من ${orders.length}` 
              : `Showing 1-${filteredOrders.length} of ${orders.length}`}
          </p>
          <div className="flex items-center gap-2">
            <button className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
              {language === 'ar' ? 'السابق' : 'Previous'}
            </button>
            <button className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium">1</button>
            <button className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">2</button>
            <button className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">3</button>
            <button className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
              {language === 'ar' ? 'التالي' : 'Next'}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
